/**
 * SessionList (2026-10-04) — các phiên chat AI Tutor của học viên trong phạm vi bài / môn đang
 * mở: chọn phiên cũ để xem lại + hỏi tiếp, hoặc mở phiên mới. Mới nhất lên đầu.
 */

import type { KeyboardEvent } from "react";
import { Button, Empty, List, Typography } from "antd";
import { MessageOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/vi";
import type { Conversation } from "@/api/learning";

dayjs.extend(relativeTime);

const { Text } = Typography;

/** Tiêu đề phiên = câu hỏi đầu tiên (backend cắt 80 ký tự, dạng markdown) → bỏ ký hiệu markdown. */
export function sessionTitle(conv: Conversation): string {
  const plain = conv.title
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/:?-{3,}:?/g, " ")
    .replace(/[`*_~#>|\\$]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain || "Phiên mới";
}

interface Props {
  sessions: Conversation[];
  activeId: number | undefined;
  onSelect: (id: number) => void;
  onNew: () => void;
  loading?: boolean;
}

export function SessionList({ sessions, activeId, onSelect, onNew, loading }: Props) {
  return (
    <div
      data-testid="ai-tutor-sessions"
      style={{ flex: 1, overflowY: "auto", padding: 12, background: "#fafafa" }}
    >
      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={onNew}
        style={{ marginBottom: 12 }}
      >
        Phiên chat mới
      </Button>
      <List
        loading={loading}
        split={false}
        dataSource={sessions}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có phiên chat nào" />
          ),
        }}
        renderItem={(conv) => {
          const active = conv.id === activeId;
          return (
            <List.Item
              data-testid="ai-tutor-session"
              role="button"
              tabIndex={0}
              aria-current={active || undefined}
              onClick={() => onSelect(conv.id)}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(conv.id);
                }
              }}
              style={{
                cursor: "pointer",
                padding: "10px 12px",
                marginBottom: 6,
                borderRadius: 8,
                background: active ? "#f9f0ff" : "#fff",
                border: `1px solid ${active ? "#d3adf7" : "#f0f0f0"}`,
              }}
            >
              <List.Item.Meta
                avatar={<MessageOutlined style={{ color: "#722ed1", marginTop: 4 }} />}
                title={
                  <Text ellipsis strong={active} style={{ maxWidth: "100%" }}>
                    {sessionTitle(conv)}
                  </Text>
                }
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {conv.message_count} tin nhắn ·{" "}
                    {dayjs(conv.last_message_at).locale("vi").fromNow()}
                  </Text>
                }
              />
            </List.Item>
          );
        }}
      />
    </div>
  );
}
